import React, { useEffect, useState } from 'react';
import ezcashHomeScreen from './assets/ezcash/home-screen.png';
import ezcashReceiveScreen from './assets/ezcash/receive-screen.png';
import ezcashSendFlow from './assets/ezcash/send-flow.png';
import ezcashTransactionList from './assets/ezcash/transaction-list.png';
import ezcashLogo from './ezcash.svg';
import './App.css';

type PageId = 'home' | 'moneystream' | 'consulting' | 'ezcash' | 'contact';

type PageLink = {
	id: PageId;
	label: string;
	kicker: string;
	description: string;
};

type DesktopPlatformKey =
	| 'windows-x86_64'
	| 'windows-aarch64'
	| 'darwin-x86_64'
	| 'darwin-aarch64'
	| 'linux-x86_64'
	| 'linux-aarch64';

type ClientPlatform = {
	os: 'windows' | 'darwin' | 'linux' | 'android' | 'ios' | 'unknown';
	arch: 'x86_64' | 'aarch64' | 'unknown';
};

type DownloadOption = {
	key: DesktopPlatformKey;
	label: string;
	url: string;
	isRecommended: boolean;
};

type StoreDownloadOption = {
	id: 'android' | 'ios';
	label: string;
	url: string;
};

type NavigatorWithUserAgentData = Navigator & {
	userAgentData?: {
		platform?: string;
		getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string; platform?: string }>;
	};
};

const desktopPlatformLabels: Record<DesktopPlatformKey, string> = {
	'windows-x86_64': 'Windows (x64)',
	'windows-aarch64': 'Windows (Arm64)',
	'darwin-x86_64': 'macOS (Intel)',
	'darwin-aarch64': 'macOS (Apple silicon)',
	'linux-x86_64': 'Linux (x64)',
	'linux-aarch64': 'Linux (Arm64)',
};

const desktopPlatformOrder: DesktopPlatformKey[] = [
	'windows-x86_64',
	'windows-aarch64',
	'darwin-aarch64',
	'darwin-x86_64',
	'linux-x86_64',
	'linux-aarch64',
];

const pageLinks: PageLink[] = [
	{
		id: 'home',
		label: 'Overview',
		kicker: 'IronPigeon',
		description: 'Privacy-focused desktop software and consulting services.',
	},
	{
		id: 'moneystream',
		label: 'MoneyStream',
		kicker: 'Personal finance',
		description: 'Personal finance management for customers who want modern convenience without surrendering their data.',
	},
	{
		id: 'consulting',
		label: 'Consulting',
		kicker: 'Services',
		description: 'Architecture, libraries, desktop apps, and OSS support.',
	},
	{
		id: 'ezcash',
		label: 'eZcash',
		kicker: 'Shielded Zcash wallet',
		description: 'A privacy-focused Zcash wallet across desktop and mobile.',
	},
	{
		id: 'contact',
		label: 'Get in touch',
		kicker: 'Contact',
		description: 'All contact options for product feedback, consulting, and direct outreach.',
	},
];

const sectionAnchors: Partial<Record<string, PageId>> = {
	'moneystream-download': 'moneystream',
	'ezcash-download': 'ezcash',
};

const isPageId = (value: string): value is PageId =>
	pageLinks.some((page) => page.id === value);

const getHashValue = (): string =>
	window.location.hash.replace('#', '');

const getPageFromHashTarget = (): PageId => {
	const hash = getHashValue();
	if (isPageId(hash)) {
		return hash;
	}

	return sectionAnchors[hash] ?? 'home';
};


const consultingSpecialties = [
	'C# libraries and desktop applications',
	'Open source maintainer support and sustainment work',
	'Architecture reviews for security, privacy, and maintainability',
	'TypeScript, PowerShell, Rust, and adjacent platform tooling',
];

const consultingContactMethods = [
	{
		label: 'Email',
		href: 'mailto:andrewarnott@live.com',
		value: 'andrewarnott@live.com',
	},
	{
		label: 'Signal',
		href: 'https://signal.me/#eu/Nl__xnl6E-pzt0Xb8PzYDXDBUVdcdmWqSbadLuqW9Br79J2OMHNN8qlAUZ28Zr5y',
		value: '@aarnott.55',
	},
	{
		label: 'X',
		href: 'https://x.com/aarnott',
		value: '@aarnott',
	},
	{
		label: 'Matrix',
		href: 'https://matrix.to/#/@aarnott:matrix.org',
		value: '@aarnott:matrix.org',
	},
	{
		label: 'Telegram',
		href: 'https://t.me/aarnott',
		value: 't.me/aarnott',
	},
	{
		label: 'Keybase',
		href: 'https://keybase.io/aarnott',
		value: 'keybase.io/aarnott',
	},
];

const mailingAddress = 'IronPigeon, LLC, 1704 Roma Ct., Longmont, CO 80503.';

const consultingFaqs = [
	{
		title: 'When paid support applies',
		copy: 'Paid engagements are for your project work: specialized support, uncommon use cases, proprietary code reviews, or requests that need more time than open source maintenance budgets allow.',
	},
	{
		title: 'How open source support still works',
		copy: 'Bug reports and pull requests still get a discretionary once-over, and work that is broadly useful or personally interesting may still get free attention.',
	},
];

const ezcashFeatures = [
	'Auto-shielding by default, with Orchard and Sapling shielded pools supported.',
	'Unique diversified receiving addresses so each person you share with sees a distinct address.',
	'Send to multiple recipients at once and create or respond to payment requests.',
	'Running balance history, private memos, exchange-rate context, and wallet backup support.',
];

const ezcashPlatforms = [
	'Windows x64 and arm64 installers',
	'Linux AppImage builds for x64 and arm64',
	'macOS installers for x64 and arm64',
	'Android on Google Play and iOS on the App Store',
];

const ezcashDesktopReleaseUrls: Record<DesktopPlatformKey, string> = {
	'windows-x86_64': 'https://ezcash.blob.core.windows.net/appupdates/Nerdbank.Zcash.App-prerelease-win-x64-Setup.exe',
	'windows-aarch64': 'https://ezcash.blob.core.windows.net/appupdates/Nerdbank.Zcash.App-prerelease-win-arm64-Setup.exe',
	'darwin-x86_64': 'https://ezcash.blob.core.windows.net/appupdates/Nerdbank.Zcash.App-prerelease-osx-x64-Setup.pkg',
	'darwin-aarch64': 'https://ezcash.blob.core.windows.net/appupdates/Nerdbank.Zcash.App-prerelease-osx-arm64-Setup.pkg',
	'linux-x86_64': 'https://ezcash.blob.core.windows.net/appupdates/Nerdbank.Zcash.App-prerelease-linux-x64.AppImage',
	'linux-aarch64': 'https://ezcash.blob.core.windows.net/appupdates/Nerdbank.Zcash.App-prerelease-linux-arm64.AppImage',
};

const ezcashStoreLinks: StoreDownloadOption[] = [
	{
		id: 'android',
		label: 'Google Play',
		url: 'https://play.google.com/store/apps/details?id=com.Nerdbank.eZcash.App',
	},
	{
		id: 'ios',
		label: 'App Store',
		url: 'https://apps.apple.com/us/app/ezcash-zcash-wallet/id6596729758',
	},
];

const ezcashScreenshots = [
	{
		src: ezcashHomeScreen,
		alt: 'eZcash home screen showing balances and wallet overview',
		caption: 'Private balances and wallet status at a glance.',
	},
	{
		src: ezcashTransactionList,
		alt: 'eZcash transaction history screen with running balance',
		caption: 'Transaction history with running balances and richer context.',
	},
	{
		src: ezcashSendFlow,
		alt: 'eZcash send flow for transferring funds',
		caption: 'A focused send flow built for clear, practical transfers.',
	},
	{
		src: ezcashReceiveScreen,
		alt: 'eZcash receive screen with QR code and receiving address',
		caption: 'Receive funds with privacy-aware address handling.',
	},
];

const normalizeOs = (value: string): ClientPlatform['os'] => {
	const normalizedValue = value.toLowerCase();

	if (normalizedValue.includes('android')) {
		return 'android';
	}

	if (normalizedValue.includes('iphone') || normalizedValue.includes('ipad') || normalizedValue.includes('ios')) {
		return 'ios';
	}

	if (normalizedValue.includes('win')) {
		return 'windows';
	}

	if (normalizedValue.includes('mac') || normalizedValue.includes('darwin')) {
		return 'darwin';
	}

	if (normalizedValue.includes('linux')) {
		return 'linux';
	}

	return 'unknown';
};

const normalizeArch = (value: string): ClientPlatform['arch'] => {
	const normalizedValue = value.toLowerCase();

	if (normalizedValue.includes('arm') || normalizedValue.includes('aarch64')) {
		return 'aarch64';
	}

	if (
		normalizedValue.includes('x64') ||
		normalizedValue.includes('x86_64') ||
		normalizedValue.includes('amd64') ||
		normalizedValue.includes('win64')
	) {
		return 'x86_64';
	}

	return 'unknown';
};

const detectClientPlatform = async (): Promise<ClientPlatform> => {
	const navigatorWithHints = navigator as NavigatorWithUserAgentData;
	const userAgent = navigator.userAgent || '';
	let osHint = `${navigatorWithHints.userAgentData?.platform || ''} ${navigator.platform || ''} ${userAgent}`;
	let archHint = `${navigator.platform || ''} ${userAgent}`;

	if (navigatorWithHints.userAgentData?.getHighEntropyValues) {
		try {
			const highEntropyValues = await navigatorWithHints.userAgentData.getHighEntropyValues(['architecture', 'platform']);
			osHint = `${osHint} ${highEntropyValues.platform || ''}`;
			archHint = `${archHint} ${highEntropyValues.architecture || ''}`;
		} catch {
			// Fallback to the user agent string when client hints are unavailable.
		}
	}

	return {
		os: normalizeOs(osHint),
		arch: normalizeArch(archHint),
	};
};

const getRecommendedDownloadKey = (
	platforms: Partial<Record<DesktopPlatformKey, unknown>>,
	clientPlatform: ClientPlatform,
): DesktopPlatformKey | null => {
	if (clientPlatform.os === 'unknown') {
		return desktopPlatformOrder.find((key) => platforms[key]) ?? null;
	}

	const exactKey = `${clientPlatform.os}-${clientPlatform.arch}` as DesktopPlatformKey;
	if (platforms[exactKey]) {
		return exactKey;
	}

	const x64FallbackKey = `${clientPlatform.os}-x86_64` as DesktopPlatformKey;
	if (platforms[x64FallbackKey]) {
		return x64FallbackKey;
	}

	const armFallbackKey = `${clientPlatform.os}-aarch64` as DesktopPlatformKey;
	if (platforms[armFallbackKey]) {
		return armFallbackKey;
	}

	return desktopPlatformOrder.find((key) => platforms[key]) ?? null;
};

function App() {
	const [activePage, setActivePage] = useState<PageId>(() => getPageFromHashTarget());
	const [ezcashClientPlatform, setEzcashClientPlatform] = useState<ClientPlatform | null>(null);
	const [ezcashRecommendedDownloadKey, setEzcashRecommendedDownloadKey] = useState<DesktopPlatformKey | null>(null);
	const [ezcashSlideIndex, setEzcashSlideIndex] = useState(0);
	const [ezcashSlideshowInteracted, setEzcashSlideshowInteracted] = useState(false);
	const [showAllEzcashDownloads, setShowAllEzcashDownloads] = useState(false);

	useEffect(() => {
		const onHashChange = () => {
			setActivePage(getPageFromHashTarget());
		};

		window.addEventListener('hashchange', onHashChange);
		return () => {
			window.removeEventListener('hashchange', onHashChange);
		};
	}, []);

	useEffect(() => {
		const hash = getHashValue();
		if (!hash || isPageId(hash)) {
			return;
		}

		const target = document.getElementById(hash);
		if (target) {
			target.scrollIntoView();
		}
	}, [activePage]);

	useEffect(() => {
		let isDisposed = false;

		const detectPreferredEzcashDownload = async () => {
			const clientPlatform = await detectClientPlatform();
			const recommendation = getRecommendedDownloadKey(ezcashDesktopReleaseUrls, clientPlatform);

			if (!isDisposed) {
				setEzcashClientPlatform(clientPlatform);
				setEzcashRecommendedDownloadKey(recommendation);
			}
		};

		detectPreferredEzcashDownload();

		return () => {
			isDisposed = true;
		};
	}, []);

	useEffect(() => {
		if (activePage !== 'ezcash' || ezcashSlideshowInteracted) {
			return;
		}

		const timerId = window.setInterval(() => {
			setEzcashSlideIndex((currentIndex) =>
				(currentIndex + 1) % ezcashScreenshots.length,
			);
		}, 3000);

		return () => {
			window.clearInterval(timerId);
		};
	}, [activePage, ezcashSlideshowInteracted]);

	const activePageMeta = pageLinks.find((page) => page.id === activePage) ?? pageLinks[0];
	const ezcashDesktopDownloads: DownloadOption[] = desktopPlatformOrder.map((key) => ({
		key,
		label: desktopPlatformLabels[key],
		url: ezcashDesktopReleaseUrls[key],
		isRecommended: key === ezcashRecommendedDownloadKey,
	}));
	const recommendedEzcashDesktopDownload =
		ezcashDesktopDownloads.find((download) => download.isRecommended) ?? ezcashDesktopDownloads[0] ?? null;
	const recommendedEzcashStoreDownload =
		ezcashClientPlatform?.os === 'android'
			? ezcashStoreLinks.find((download) => download.id === 'android') ?? null
			: ezcashClientPlatform?.os === 'ios'
				? ezcashStoreLinks.find((download) => download.id === 'ios') ?? null
				: null;
	const recommendedEzcashDownload = recommendedEzcashStoreDownload ?? recommendedEzcashDesktopDownload;
	const ezcashRecommendedLabel =
		recommendedEzcashStoreDownload?.label ?? recommendedEzcashDesktopDownload?.label ?? null;
	const ezcashAdditionalDownloads = [
		...ezcashDesktopDownloads.filter((download) => download.url !== recommendedEzcashDownload?.url),
		...ezcashStoreLinks.filter((download) => download.url !== recommendedEzcashStoreDownload?.url),
	];
	const activeEzcashScreenshot = ezcashScreenshots[ezcashSlideIndex];

	const markEzcashSlideshowInteracted = () => {
		setEzcashSlideshowInteracted(true);
	};

	const showPreviousEzcashSlide = () => {
		markEzcashSlideshowInteracted();
		setEzcashSlideIndex((currentIndex) =>
			(currentIndex - 1 + ezcashScreenshots.length) % ezcashScreenshots.length,
		);
	};

	const showNextEzcashSlide = () => {
		markEzcashSlideshowInteracted();
		setEzcashSlideIndex((currentIndex) =>
			(currentIndex + 1) % ezcashScreenshots.length,
		);
	};

	const contactPageHref = '#contact';

	return (
		<div className="site-shell">
			<div className="site-backdrop site-backdrop-left" />
			<div className="site-backdrop site-backdrop-right" />
			<header className="site-header">
				<a className="brand" href="#home">
					<span className="brand-mark">IP</span>
					<span>
						<strong>IronPigeon</strong>
						<small>Privacy-first software products and consulting</small>
					</span>
				</a>
				<nav className="site-nav" aria-label="Primary">
					{pageLinks.map((page) => (
						<a
							key={page.id}
							href={`#${page.id}`}
							className={page.id === activePage ? 'is-active' : undefined}
						>
							{page.label}
						</a>
					))}
				</nav>
			</header>

			<main className="page-frame">
				<section className="hero-panel">
					<div className="hero-copy">
						<p className="eyebrow">{activePageMeta.kicker}</p>
						<h1>Build your money systems without handing over your privacy</h1>
						<p className="hero-summary">{activePageMeta.description}</p>
						<div className="hero-actions">
							<a className="primary-cta" href="#moneystream">Explore MoneyStream</a>
							<a className="secondary-cta" href="#consulting">See consulting services</a>
						</div>
					</div>
					<div className="hero-card-stack" aria-hidden="true">
						<div className="hero-card hero-card-main">
							<p>MoneyStream</p>
							<strong>Local-first finance for all major desktop OSs</strong>
							<span>Budgets, transactions, sync, connected banking, and investment reporting.</span>
						</div>
						<div className="hero-card hero-card-side">
							<p>Consulting</p>
							<strong>Architecture depth with OSS credibility</strong>
						</div>
					</div>
				</section>

				<section className="page-grid">
					<aside className="page-index" aria-label="Page summary">
						{pageLinks.map((page) => (
							<a key={page.id} href={`#${page.id}`} className={page.id === activePage ? 'is-active' : undefined}>
								<span>{page.kicker}</span>
								<strong>{page.label}</strong>
								<small>{page.description}</small>
							</a>
						))}
					</aside>

					<div className="page-content">
						{activePage === 'home' && (
							<>
								<section className="content-panel lead-panel">
									<p className="eyebrow">What IronPigeon builds</p>
									<h2>IronPigeon builds privacy-first software for personal finance, consulting, and specialized digital asset use</h2>
									<p>
										MoneyStream takes the lead with a personal finance experience built around privacy,
										local ownership, and optional connected services. Consulting extends that product discipline into client work,
										while eZcash serves customers who want a Zcash wallet with strong privacy defaults that works on every mobile device and desktop OS.
									</p>
								</section>
								<section className="content-panel metrics-panel">
									<div>
										<strong>3 offers</strong>
										<span>MoneyStream, consulting, and eZcash each address a distinct privacy-first need.</span>
									</div>
									<div>
										<strong>Desktop-first</strong>
										<span>IronPigeon focuses on durable software experiences for real work, not disposable web utilities.</span>
									</div>
									<div>
										<strong>30+ years</strong>
										<span>Software engineering experience behind the products and consulting practice.</span>
									</div>
								</section>
								{/* <section className="card-collection">
									<article className="content-panel feature-card feature-card-primary">
										<p className="eyebrow">Personal finance</p>
										<h3>MoneyStream</h3>
										<p>Personal finance management for customers who want modern convenience without surrendering their data.</p>
										<a href="#moneystream">View product details</a>
									</article>
									<article className="content-panel feature-card">
										<p className="eyebrow">Services</p>
										<h3>Consulting</h3>
										<p>Private consulting for open source and proprietary software projects that need experienced execution.</p>
										<a href="#consulting">Review services</a>
									</article>
									<article className="content-panel feature-card feature-card-muted">
										<p className="eyebrow">Zcash wallet</p>
										<h3>eZcash</h3>
										<p>A privacy-first Zcash wallet for customers who want strong shielding defaults across desktop and mobile.</p>
										<a href="#ezcash">See eZcash</a>
									</article>
								</section> */}
							</>
						)}

						{activePage === 'moneystream' && (
							<>
								<section className="content-panel product-overview">
									<div>
										<p className="eyebrow">Personal finance</p>
										<h2>MoneyStream now has its own dedicated site</h2>
										<p>
											<em>For people who want to track their income, expenditures and investments.</em>
										</p>
										<p>
											MoneyStream anchors the IronPigeon product lineup.
											The full walkthrough, screenshots, pricing, and download details are available
											on the dedicated MoneyStream site.
										</p>
										<div className="hero-actions">
											<a className="primary-cta" href="https://moneystreamapp.com">Visit moneystreamapp.com</a>
										</div>
									</div>
								</section>
							</>
						)}

						{activePage === 'consulting' && (
							<>
								<section className="content-panel">
									<p className="eyebrow">Software consulting services</p>
									<h2>Technical support for apps, libraries, and open source projects</h2>
									<p>
										Engagements are shaped for teams that need direct execution, architecture guidance, or long-tail maintenance
										on important code. The work can support both proprietary products and the open source software they depend on.
									</p>
									<p>
										Support is available for the OSS projects already in use by your team, adjacent proprietary software,
										and focused implementation or review work where you need the original maintainer or an experienced
										architect to move quickly.
									</p>
								</section>

								<section className="content-panel split-callout">
									<div>
										<strong>Fee schedule</strong>
										<p>$85 USD per hour with a 1 hour minimum.</p>
										<p>Payment is accepted by Zcash, PayPal, and Venmo. Zcash payments receive a 20% discount.</p>
										<p>A cost estimate can usually be provided for free. Detailed estimates that require research may be billed.</p>
									</div>
									<div>
										<strong>Timing</strong>
										<p>Payment is due before work begins, with larger jobs split into milestones when appropriate.</p>
										<p>Turnaround varies by scope. Small jobs are often completed by Monday morning.</p>
									</div>
								</section>

								<section className="content-panel split-callout">
									<div>
										<strong>Experience</strong>
										<p>Over 30 years in software development, including 20 years at Microsoft, currently as a Principal Software Engineer.</p>
									</div>
									<div>
										<strong>Disclosure</strong>
										<p>Freelance work is offered independently and is not endorsed or supported by Microsoft.</p>
									</div>
								</section>

								<section className="content-panel">
									<p className="eyebrow">Contact</p>
									<h3>Start with the shared contact hub</h3>
									<p>
										Use the <a className="text-link" href={contactPageHref}>Get in touch page</a> for email,
										secure messaging, social links, and mailing details before we scope consulting work.
									</p>
								</section>

								<section className="content-panel">
									<p className="eyebrow">Best fit work</p>
									<div className="bullet-grid">
										{consultingSpecialties.map((item) => (
											<div key={item} className="bullet-card">{item}</div>
										))}
									</div>
								</section>

								<section className="content-panel">
									<p className="eyebrow">FAQ</p>
									<div className="bullet-grid">
										{consultingFaqs.map((item) => (
											<div key={item.title} className="bullet-card">
												<strong>{item.title}</strong>
												<p>{item.copy}</p>
											</div>
										))}
									</div>
								</section>
							</>
						)}

						{activePage === 'ezcash' && (
							<>
								<section className="content-panel product-overview">
									<div className="ezcash-panel">
										<div className="ezcash-brand">
											<img src={ezcashLogo} className="ezcash-logo" alt="eZcash logo" />
											<div>
												<p className="eyebrow">Shielded Zcash wallet</p>
												<h2>eZcash is a full-featured Zcash wallet across desktop and mobile</h2>
											</div>
										</div>
										<p>
											eZcash is built around practical privacy. Auto-shielding is the default, not a separate maintenance step,
											and balances stay hidden on key screens unless the user explicitly chooses to reveal them.
											The result is a wallet designed for everyday Zcash use without forcing privacy tradeoffs into the foreground.
										</p>
										<div className="bullet-grid">
											{ezcashFeatures.map((item) => (
												<div key={item} className="bullet-card">{item}</div>
											))}
										</div>
									</div>
									<div className="device-preview">
										<section id="ezcash-download" className="download-panel" aria-labelledby="ezcash-download-title">
											<p className="eyebrow">Download</p>
											<p className="download-badge">Prerelease</p>
											<h3 id="ezcash-download-title">Get the latest eZcash build for your device</h3>
											<p className="download-note">
												Desktop installers come from the current eZcash app release page and update without requiring administrator rights.
											</p>
											{recommendedEzcashDownload && (
												<>
													<p className="download-note">
														Recommended for {ezcashRecommendedLabel}. Other installers and store links are available below.
													</p>
													<div className="download-actions-row">
														<a className="primary-cta download-cta" href={recommendedEzcashDownload.url}>
															Download for {recommendedEzcashDownload.label}
														</a>
														<button
															type="button"
															className="download-toggle"
															onClick={() => setShowAllEzcashDownloads((value) => !value)}
														>
															{showAllEzcashDownloads ? 'Hide other downloads' : 'Show all downloads'}
														</button>
													</div>
													{showAllEzcashDownloads && (
														<div className="download-list" aria-label="All other eZcash downloads">
															{ezcashAdditionalDownloads.map((download) => (
																<a key={'key' in download ? download.key : download.id} className="download-link" href={download.url}>
																	<span>{download.label}</span>
																	{'isRecommended' in download && download.isRecommended && <strong>Suggested</strong>}
																</a>
															))}
														</div>
													)}
												</>
											)}
											{recommendedEzcashStoreDownload && (
												<div className="download-list" aria-label="Recommended eZcash mobile app download">
													<a key={recommendedEzcashStoreDownload.label} className="download-link" href={recommendedEzcashStoreDownload.url}>
														<span>{recommendedEzcashStoreDownload.label}</span>
													</a>
												</div>
											)}
											{!recommendedEzcashDownload && (
												<p className="download-note">Checking for the best eZcash download for your device.</p>
											)}
											{/* Store links remain hidden on non-mobile devices until the user asks to see all downloads. */}
											{showAllEzcashDownloads && false && (
												<div className="download-list" aria-label="eZcash mobile app downloads">
													{ezcashStoreLinks.map((download) => (
														<a key={download.label} className="download-link" href={download.url}>
															<span>{download.label}</span>
														</a>
													))}
												</div>
											)}
										</section>
									</div>
								</section>

								<section className="content-panel ezcash-detail-grid">
									<div className="ezcash-detail-card">
										<p className="eyebrow">Availability</p>
										<h3>Install without admin rights and keep current with self-updates</h3>
										<ul>
											{ezcashPlatforms.map((item) => (
												<li key={item}>{item}</li>
											))}
										</ul>
									</div>
									<div className="ezcash-detail-card">
										<p className="eyebrow">Privacy policy</p>
										<h3>Local storage first, minimal network disclosure, and no telemetry by default</h3>
										<p>
											Normal network traffic is limited to the configured lightwallet server,
											plus optional exchange-rate providers. Crash diagnostics may be collected during failures, but not memory dumps,
											keys, addresses, transactions, or balances.
										</p>
									</div>
								</section>

								<section className="content-panel moneystream-slideshow" aria-labelledby="ezcash-screenshots-title">
									<div className="section-heading-row">
										<div>
											<p className="eyebrow">Screenshots</p>
											<h3 id="ezcash-screenshots-title">eZcash across the core wallet experience</h3>
										</div>
										<p className="slideshow-counter">{ezcashSlideIndex + 1} / {ezcashScreenshots.length}</p>
									</div>
									<div className="slideshow-controls">
										<button type="button" className="slideshow-button" onClick={showPreviousEzcashSlide}>
											Previous
										</button>
										<div className="slideshow-dots" aria-label="eZcash screenshots">
											{ezcashScreenshots.map((shot, index) => (
												<button
													key={shot.alt}
													type="button"
													className={`slideshow-dot${index === ezcashSlideIndex ? ' is-active' : ''}`}
													onClick={() => {
														markEzcashSlideshowInteracted();
														setEzcashSlideIndex(index);
													}}
													aria-label={`Show eZcash screenshot ${index + 1}`}
												/>
											))}
										</div>
										<button type="button" className="slideshow-button" onClick={showNextEzcashSlide}>
											Next
										</button>
									</div>
									<figure className="slideshow-figure">
										<button
											type="button"
											className="slideshow-image-button"
											onClick={showNextEzcashSlide}
											aria-label="Show next eZcash screenshot"
										>
											<img src={activeEzcashScreenshot.src} alt={activeEzcashScreenshot.alt} loading="lazy" />
										</button>
										<figcaption>{activeEzcashScreenshot.caption}</figcaption>
									</figure>
								</section>
							</>
						)}

						{activePage === 'contact' && (
							<>
								<section className="content-panel">
									<p className="eyebrow">Get in touch</p>
									<h2>Choose the contact channel that fits the conversation</h2>
									<p>
										Use these contact options for MoneyStream feedback, consulting inquiries, product questions,
										or direct outreach. If you are already in MoneyStream, the in-app "Send feedback" command is still the fastest path for product issues.
									</p>
								</section>

								<section className="content-panel">
									<p className="eyebrow">Direct channels</p>
									<div className="bullet-grid">
										{consultingContactMethods.map((contact) => (
											<div key={contact.label} className="bullet-card">
												<strong>{contact.label}</strong>
												<p>
													<a className="text-link" href={contact.href}>{contact.value}</a>
												</p>
											</div>
										))}
									</div>
								</section>

								<section className="content-panel split-callout">
									<div>
										<strong>Mailing address</strong>
										<p>{mailingAddress}</p>
									</div>
									<div>
										<strong>Best use</strong>
										<p>General product questions, consulting inquiries, support follow-up, and direct introductions all start here.</p>
									</div>
								</section>
							</>
						)}
					</div>
				</section>
			</main>
		</div>
	);
}

export default App;
